import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, CircleMarker, useMap, useMapEvents, LayersControl, Polyline, Polygon } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  MapPin,
  Wifi,
  WifiOff,
  Plus,
  Maximize2,
  Ship,
  Activity,
  History,
  Filter,
  AlertTriangle,
  Cloud,
  Navigation,
  ChevronDown,
  X,
  Route,
  Eye,
  EyeOff
} from 'lucide-react';
import { cn, formatShortDateTime } from '@/lib/utils';
// FASE 5 + LAYER 2 FIX: Import SignalK mapping utility com conversões
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useTimezone } from '@/contexts/TimezoneContext';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { geofenceApi } from '@/services/geofenceApi';

import { OctopusIcon } from '@/components/icons/CustomIcons';

const { BaseLayer } = LayersControl;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCKET_PATH = '/api/socket.io';

// SVG paths for custom icons (line-art style)
const customIconSvgs = {
  ship: `<path d="M3 15 L5 19 L19 19 L21 15 L3 15" /><rect x="7" y="10" width="10" height="5" /><rect x="9" y="6" width="6" height="4" /><rect x="15" y="4" width="2" height="6" /><path d="M16 4 C16 2 18 2 18 4" /><path d="M2 21 C4 20 6 22 8 21 C10 20 12 22 14 21 C16 20 18 22 20 21" />`,
  // Draga: Embarcação com roda de pás e tubo de descarga
  dredge: `<path d="M2 16 L4 18 L20 18 L22 16 L2 16" /><path d="M2 16 L2 13 L5 13" /><rect x="4" y="10" width="4" height="6" /><path d="M5 11 L7 11" /><path d="M6 10 L6 6" /><circle cx="13" cy="12" r="4" /><path d="M13 8 L13 16" /><path d="M9 12 L17 12" /><circle cx="13" cy="12" r="1" /><path d="M18 10 L18 7 L19 7 L19 10" /><path d="M20 14 L22 15 L22 17" /><path d="M1 20 C3 19 5 21 7 20 C9 19 11 21 13 20 C15 19 17 21 19 20" />`,
  loader: `<rect x="10" y="6" width="6" height="6" rx="1" /><path d="M8 12 L18 12 L18 16 L8 16 Z" /><path d="M10 10 L4 6 L2 8 L6 12" /><path d="M2 8 L1 5 L5 4 L4 6" /><circle cx="15" cy="18" r="3" /><circle cx="7" cy="18" r="2.5" />`,
  // Motor Estacionário / Gerador: Motor com alternador acoplado
  engine: `<path d="M2 20 L22 20" /><path d="M3 20 L3 18" /><path d="M21 20 L21 18" /><rect x="3" y="10" width="8" height="8" rx="1" /><path d="M4 10 L4 8 L10 8 L10 10" /><path d="M5 8 L5 6 L9 6 L9 8" /><path d="M3 11 L1 11 L1 14" /><rect x="11" y="12" width="2" height="4" /><ellipse cx="17" cy="14" rx="4" ry="4" /><ellipse cx="17" cy="14" rx="2.5" ry="2.5" /><circle cx="17" cy="14" r="1" /><rect x="19" y="10" width="3" height="3" /><rect x="14" y="6" width="5" height="3" />`,
  car: `<path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/>`,
  truck: `<path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/>`,
  settings: `<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>`,
};

const MARKER_COLORS = {
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  blue: '#0ea5e9',
  gray: '#64748b',
};

const createGeofenceWaypointIcon = (color = '#0EA5E9') =>
  L.divIcon({
    className: 'dashboard-geofence-waypoint-marker',
    html: `
      <div style="
        width: 14px;
        height: 14px;
        border-radius: 9999px;
        border: 2px solid ${color};
        background: rgba(2, 6, 23, 0.95);
        box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.12);
      "></div>
    `,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });

const getGeofencePathOptions = (gf, fallbackOpacity = 0.15) => {
  const fillEnabled = gf?.fill_enabled !== false;
  const fillOpacity = Number.isFinite(Number(gf?.fill_opacity))
    ? Number(gf.fill_opacity)
    : fallbackOpacity;
  return {
    color: gf?.border_color || gf?.color || '#0EA5E9',
    fillColor: gf?.color || '#0EA5E9',
    fillOpacity: fillEnabled ? fillOpacity : 0,
    weight: Number(gf?.border_width || 1),
  };
};

const computeMarkerColor = (equipment = {}) => {
  const status = equipment.status || 'offline';
  const operationalStatus = equipment.operational_status || 'active';
  const alertsCount = equipment.active_alerts_count || 0;

  if (status === 'awaiting_connection' || equipment.provisioning_status === 'pending') {
    return 'blue';
  }
  if (status === 'offline') {
    return 'gray';
  }
  if (operationalStatus === 'inactive') {
    return 'gray';
  }
  if (operationalStatus === 'maintenance') {
    return 'yellow';
  }
  if (alertsCount > 0) {
    return equipment.marker_color === 'red' ? 'red' : 'yellow';
  }
  return 'green';
};

const getEquipmentStatusLabel = (status, t) => {
  if (status === 'awaiting_connection') return t('awaitingInitialConnection');
  if (status === 'online') return t('online');
  return t('offline');
};

const TRAIL_GAP_THRESHOLD_MS = 10 * 60 * 1000;

const splitTrailSegments = (points, gapThresholdMs = TRAIL_GAP_THRESHOLD_MS) => {
  if (!Array.isArray(points) || points.length < 2) return [];

  const normalized = points
    .filter((p) => p?.lat != null && p?.lon != null && p?.time)
    .map((p) => {
      const ts = new Date(p.time).getTime();
      return Number.isFinite(ts) ? { ...p, _ts: ts } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a._ts - b._ts);

  if (normalized.length < 2) return [];

  const segments = [];
  let current = [normalized[0]];

  for (let i = 1; i < normalized.length; i += 1) {
    const point = normalized[i];
    const prev = normalized[i - 1];

    if (point._ts - prev._ts > gapThresholdMs) {
      if (current.length > 1) {
        segments.push(current.map((p) => [p.lat, p.lon]));
      }
      current = [point];
    } else {
      current.push(point);
    }
  }

  if (current.length > 1) {
    segments.push(current.map((p) => [p.lat, p.lon]));
  }

  return segments;
};

const MAX_HOVER_POINTS = 1500;
const TRAIL_HOVER_SNAP_PX = 18;
const TRAIL_HOVER_FRAME_MS = 50;
const TRAIL_BASE_COLOR = '#a855f7';
const TRAIL_DASH_PATTERN = '2 4';

const getTrailPathOptions = () => ({
  color: TRAIL_BASE_COLOR,
  weight: 3,
  opacity: 0.8,
  dashArray: TRAIL_DASH_PATTERN,
  lineCap: 'round',
  lineJoin: 'round',
});

const buildHoverPoints = (points, maxPoints = MAX_HOVER_POINTS) => {
  if (!Array.isArray(points) || points.length === 0) return [];
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  const sampled = [];
  for (let i = 0; i < points.length; i += step) {
    sampled.push(points[i]);
  }
  const last = points[points.length - 1];
  if (sampled[sampled.length - 1] !== last) sampled.push(last);
  return sampled;
};

const getDirectionArrow = (degrees) => {
  if (degrees == null || Number.isNaN(Number(degrees))) return '•';
  const normalized = ((Number(degrees) % 360) + 360) % 360;
  if (normalized >= 337.5 || normalized < 22.5) return '↑';
  if (normalized < 67.5) return '↗';
  if (normalized < 112.5) return '→';
  if (normalized < 157.5) return '↘';
  if (normalized < 202.5) return '↓';
  if (normalized < 247.5) return '↙';
  if (normalized < 292.5) return '←';
  return '↖';
};

const setTrailCursor = (event, cursor = '') => {
  const map = event?.target?._map;
  if (!map) return;
  map.getContainer().style.cursor = cursor;
};

const getIconKeyFromTypeName = (typeName) => {
  if (!typeName) return 'settings';
  const name = typeName.toLowerCase();
  if (name.includes('rebocador') || name.includes('ship') || name.includes('embarcação')) return 'ship';
  if (name.includes('draga')) return 'dredge';
  if (name.includes('carregadeira') || name.includes('loader')) return 'loader';
  if (name.includes('motor') || name.includes('estacionário') || name.includes('engine')) return 'engine';
  if (name.includes('caminhão') || name.includes('truck')) return 'truck';
  if (name.includes('veículo') || name.includes('leve') || name.includes('car')) return 'car';
  return 'settings';
};

const createEquipmentIcon = (typeName, markerColor, heading, cog, speedKnots, isFollowed = false) => {
  const iconKey = getIconKeyFromTypeName(typeName);
  const color = MARKER_COLORS[markerColor] || MARKER_COLORS.gray;
  const bgColor = `${color}33`;
  const svgPath = customIconSvgs[iconKey] || customIconSvgs.settings;

  // Geometria do ícone: canvas ampliado para acomodar as linhas de direção ~3x
  // mais longas sem corte. O círculo/ícone seguem do mesmo tamanho, no centro.
  const SIZE = 208;
  const C = 104;     // centro do canvas
  const LINE = 100;  // ~3x o comprimento anterior (era 34) — ajustável

  // Duas linhas de direção, distintas do trail roxo #a855f7 e entre si, com 30%
  // de transparência (stroke-opacity 0.7): proa (heading) verde claro sólido;
  // curso sobre o solo (COG) laranja tracejado. Apenas renderização dos graus já
  // convertidos no backend — sem recomputo de unidade no frontend (INV-UI-TRAIL-001).
  const directionLine = (deg, strokeColor, dashArray) =>
    `<line x1="${C}" y1="${C}" x2="${C + LINE * Math.sin((deg * Math.PI) / 180)}" y2="${C - LINE * Math.cos((deg * Math.PI) / 180)}" stroke="${strokeColor}" stroke-width="3" stroke-linecap="round" stroke-opacity="0.7"${dashArray ? ` stroke-dasharray="${dashArray}"` : ''}/>`;
  const headingLine = heading != null ? directionLine(heading, '#4ade80', '') : '';
  const showCog = cog != null && speedKnots != null && speedKnots > 0.5;
  const cogLine = showCog ? directionLine(cog, '#f97316', '5 3') : '';

  // Anel de destaque quando o equipamento está sendo seguido (modo "Seguir").
  const followRing = isFollowed
    ? `<circle cx="${C}" cy="${C}" r="24" fill="none" stroke="#38bdf8" stroke-width="2" stroke-dasharray="3 3"/>`
    : '';

  return L.divIcon({
    className: 'custom-equipment-marker',
    html: `
      <svg width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}" xmlns="http://www.w3.org/2000/svg">
        ${cogLine}
        ${headingLine}
        ${followRing}
        <circle cx="${C}" cy="${C}" r="18" fill="${bgColor}" stroke="${color}" stroke-width="2" style="pointer-events:auto;cursor:pointer"/>
        <svg x="${C - 10}" y="${C - 10}" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events:auto;cursor:pointer">
          ${svgPath}
        </svg>
      </svg>
    `,
    iconSize: [SIZE, SIZE],
    iconAnchor: [C, C],
    popupAnchor: [0, -22],
  });
};

// FitBounds component
const FitBoundsController = ({ equipments, selectedEquipment, shouldFitBounds, selectedFromSidebar, onFitComplete }) => {
  const map = useMap();
  const lastSelectedIdRef = useRef(null);
  const hasFittedRef = useRef(false);

  // Effect for sidebar selection - only triggers when user clicks sidebar
  useEffect(() => {
    if (!selectedFromSidebar) return;
    if (!selectedEquipment || !selectedEquipment.latitude || !selectedEquipment.longitude) return;
    
    // Only center once per selection
    if (lastSelectedIdRef.current !== selectedEquipment.id) {
      map.setView([selectedEquipment.latitude, selectedEquipment.longitude], 14);
      lastSelectedIdRef.current = selectedEquipment.id;
    }
  }, [selectedEquipment, selectedFromSidebar, map]);

  // Effect for "Fit All" button - triggered explicitly
  useEffect(() => {
    if (!shouldFitBounds) return;
    if (equipments.length === 0) return;
    
    const eqsWithLoc = equipments.filter(eq => eq.latitude && eq.longitude);
    if (eqsWithLoc.length === 0) return;
    
    if (eqsWithLoc.length === 1) {
      map.setView([eqsWithLoc[0].latitude, eqsWithLoc[0].longitude], 12);
    } else {
      const bounds = L.latLngBounds(eqsWithLoc.map(eq => [eq.latitude, eq.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
    hasFittedRef.current = true;
    // Callback to reset shouldFitBounds after fitting
    if (onFitComplete) onFitComplete();
  }, [shouldFitBounds, equipments, map, onFitComplete]);

  // Initial fit - only once when equipments first load
  useEffect(() => {
    if (hasFittedRef.current) return;
    if (equipments.length === 0) return;
    
    const eqsWithLoc = equipments.filter(eq => eq.latitude && eq.longitude);
    if (eqsWithLoc.length === 0) return;
    
    if (eqsWithLoc.length === 1) {
      map.setView([eqsWithLoc[0].latitude, eqsWithLoc[0].longitude], 12);
    } else {
      const bounds = L.latLngBounds(eqsWithLoc.map(eq => [eq.latitude, eq.longitude]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
    }
    hasFittedRef.current = true;
  }, [equipments, map]);

  useEffect(() => {
    if (!selectedEquipment) lastSelectedIdRef.current = null;
  }, [selectedEquipment]);

  return null;
};

// Follow component: mantém o mapa centralizado no conjunto de equipamentos seguidos.
// Enquadra (fitBounds) uma vez quando o conjunto muda, estabelecendo o zoom inicial;
// depois só re-centraliza (panTo) a cada atualização de posição, preservando o zoom
// do usuário. O gesto de arrastar (dragstart) desengata; o zoom NÃO desengata.
// panTo/fitBounds programáticos não disparam 'dragstart', então não se auto-desligam.
const FollowController = ({ equipments, followedIds, onDisengage }) => {
  const map = useMap();
  const framedKeyRef = useRef(null);
  const lastCenterRef = useRef(null);

  // Desengata o follow quando o usuário arrasta o mapa.
  useEffect(() => {
    const handleDragStart = () => onDisengage();
    map.on('dragstart', handleDragStart);
    return () => map.off('dragstart', handleDragStart);
  }, [map, onDisengage]);

  useEffect(() => {
    if (!followedIds || followedIds.size === 0) {
      framedKeyRef.current = null;
      lastCenterRef.current = null;
      return;
    }
    const points = equipments
      .filter(eq => followedIds.has(eq.id) && eq.latitude && eq.longitude)
      .map(eq => [eq.latitude, eq.longitude]);
    if (points.length === 0) return;

    const key = Array.from(followedIds).sort().join(',');
    const bounds = L.latLngBounds(points);
    const center = bounds.getCenter();
    const centerKey = `${center.lat.toFixed(6)},${center.lng.toFixed(6)}`;

    if (framedKeyRef.current !== key) {
      // Conjunto mudou: enquadra uma vez (define o zoom inicial do acompanhamento).
      if (points.length === 1) {
        map.setView(points[0], 14);
      } else {
        map.fitBounds(bounds, { padding: [60, 60], maxZoom: 15 });
      }
      framedKeyRef.current = key;
      lastCenterRef.current = centerKey;
    } else if (lastCenterRef.current !== centerKey) {
      // Mesmo conjunto, posição nova: só re-centraliza mantendo o zoom do usuário.
      // (O efeito roda a cada render; o guard de centro evita panTo redundante.)
      lastCenterRef.current = centerKey;
      map.panTo(center, { animate: true, duration: 0.5 });
    }
  }, [equipments, followedIds, map]);

  return null;
};

const TrailHoverController = ({ enabled, points, onHoverChange, onPointClick }) => {
  const map = useMap();
  const pointsRef = useRef(points || []);
  const rafRef = useRef(null);
  const pendingLatLngRef = useRef(null);
  const lastResolveRef = useRef(0);
  const hoveredKeyRef = useRef('');
  const hoveredPointRef = useRef(null);

  useEffect(() => {
    pointsRef.current = points || [];
  }, [points]);

  const clearHover = useCallback(() => {
    hoveredKeyRef.current = '';
    hoveredPointRef.current = null;
    onHoverChange(null);
    map.getContainer().style.cursor = '';
  }, [map, onHoverChange]);

  const resolveNearest = useCallback((latlng) => {
    const activePoints = pointsRef.current || [];
    if (!enabled || !latlng || activePoints.length === 0) {
      clearHover();
      return;
    }

    const mousePoint = map.latLngToContainerPoint(latlng);
    let nearest = null;
    let nearestDistSq = Number.POSITIVE_INFINITY;

    for (let i = 0; i < activePoints.length; i += 1) {
      const p = activePoints[i];
      if (p?.lat == null || p?.lon == null) continue;
      const projected = map.latLngToContainerPoint([p.lat, p.lon]);
      const dx = projected.x - mousePoint.x;
      const dy = projected.y - mousePoint.y;
      const distSq = (dx * dx) + (dy * dy);
      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearest = p;
      }
    }

    const thresholdSq = TRAIL_HOVER_SNAP_PX * TRAIL_HOVER_SNAP_PX;
    const selectedPoint = nearestDistSq <= thresholdSq ? nearest : null;
    const selectedKey = selectedPoint
      ? `${selectedPoint.equipmentId}:${selectedPoint.time}:${selectedPoint.lat}:${selectedPoint.lon}`
      : '';

    if (selectedKey !== hoveredKeyRef.current) {
      hoveredKeyRef.current = selectedKey;
      hoveredPointRef.current = selectedPoint;
      onHoverChange(selectedPoint);
    }

    map.getContainer().style.cursor = selectedPoint ? 'crosshair' : '';
  }, [clearHover, enabled, map, onHoverChange]);

  useMapEvents({
    mousemove: (event) => {
      if (!enabled) {
        clearHover();
        return;
      }
      pendingLatLngRef.current = event.latlng;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const now = Date.now();
        if (now - lastResolveRef.current < TRAIL_HOVER_FRAME_MS) return;
        lastResolveRef.current = now;
        resolveNearest(pendingLatLngRef.current);
      });
    },
    mouseout: () => {
      clearHover();
    },
    click: () => {
      if (!enabled || !hoveredPointRef.current) return;
      onPointClick(hoveredPointRef.current);
    },
  });

  useEffect(() => {
    if (!enabled) {
      clearHover();
      return;
    }
    hoveredKeyRef.current = '';
    hoveredPointRef.current = null;
    onHoverChange(null);
    map.getContainer().style.cursor = '';
  }, [clearHover, enabled, map, onHoverChange]);

  useEffect(() => () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    map.getContainer().style.cursor = '';
  }, [map]);

  return null;
};

// Converte o valor de <input type="datetime-local"> (naive, sem fuso) para ISO
// UTC interpretando-o no fuso IANA CONFIGURADO — o rótulo "TZ:" ao lado do input
// promete o fuso do sistema (Brasil/Brasília), não o do navegador (a máquina do
// operador pode estar em outro fuso — fix 2026-07-04). Duas passadas de correção
// via Intl cobrem transições de DST; fuso inválido → fallback no parse nativo.
const naiveLocalToUtcIso = (naive, timeZone) => {
  if (!naive) return null;
  const m = String(naive).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  const [, y, mo, d, hh, mm] = m.map(Number);
  const desired = Date.UTC(y, mo - 1, d, hh, mm);
  let utc = desired;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
    for (let i = 0; i < 2; i += 1) {
      const parts = fmt.formatToParts(new Date(utc));
      const get = (t) => Number(parts.find((p) => p.type === t)?.value);
      const asInTz = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
      utc += desired - asInTz;
    }
  } catch {
    const fallback = new Date(naive);
    return Number.isNaN(fallback.getTime()) ? null : fallback.toISOString();
  }
  return new Date(utc).toISOString();
};

const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatDateTime, timezoneLabel, timezone } = useTimezone();
  // Unidades dos campos *_display da trilha (preferência aplicada no backend —
  // INV-UI-TRAIL-001: o frontend só renderiza, nunca converte).
  const [trailDisplayUnits, setTrailDisplayUnits] = useState({});
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [shouldFitBounds, setShouldFitBounds] = useState(true);
  const [selectedFromSidebar, setSelectedFromSidebar] = useState(false);
  // Modo "Seguir": trava o mapa acompanhando um conjunto de equipamentos.
  const [followMode, setFollowMode] = useState(false);
  const [followedIds, setFollowedIds] = useState(() => new Set());
  const [filters, setFilters] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [trailData, setTrailData] = useState(null);
  const [trailVisible, setTrailVisible] = useState(false);
  const [trailLoading, setTrailLoading] = useState(false);
  const [trailHours, setTrailHours] = useState('24');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customRangeApplied, setCustomRangeApplied] = useState(null);
  const [globalTrailEnabled, setGlobalTrailEnabled] = useState(false);
  const [allTrails, setAllTrails] = useState({});  // { equipmentId: { points: [] } }
  const [systemAlerts, setSystemAlerts] = useState([]);  // v3.12.0: Alertas do sistema (disco, etc)
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());  // Alertas dispensados na sessão
  const [geofences, setGeofences] = useState([]);  // v3.11.0: Geofences para overlay no mapa
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotData, setSnapshotData] = useState(null);
  const [snapshotPoint, setSnapshotPoint] = useState(null);
  const mapRef = useRef(null);
  const socketRef = useRef(null);
  const trailRequestRef = useRef(0);
  const allTrailsRequestRef = useRef(0);
  const initialGlobalTrailLoadedRef = useRef(false);
  const equipmentsRef = useRef([]);

  useEffect(() => {
    equipmentsRef.current = equipments;
  }, [equipments]);

  const fetchMapData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeFilters.operational_status) params.set('operational_status', activeFilters.operational_status);
      if (activeFilters.category) params.set('category', activeFilters.category);
      if (activeFilters.equipment_group_id) params.set('equipment_group_id', activeFilters.equipment_group_id);
      if (activeFilters.include_inactive) params.set('include_inactive', 'true');

      const qs = params.toString();
      const response = await axios.get(`${API}/dashboard/map-data${qs ? `?${qs}` : ''}`);
      setEquipments(response.data || []);
    } catch (error) {
      console.error('Error fetching map data:', error);
      setEquipments([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilters]);

  const fetchFilters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  }, []);

  const fetchSummary = useCallback(async (equipmentId) => {
    setSummaryLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/equipment/${equipmentId}/summary`);
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchTrail = useCallback(async (equipmentId, hours = null, customRange = null) => {
    const hoursToUse = String(hours || trailHours);
    const effectiveCustomRange = customRange || customRangeApplied;
    const requestId = ++trailRequestRef.current;
    setTrailLoading(true);
    try {
      const params = {};
      if (hoursToUse === 'custom') {
        if (!effectiveCustomRange?.startIso || !effectiveCustomRange?.endIso) {
          setTrailLoading(false);
          return;
        }
        params.start_iso = effectiveCustomRange.startIso;
        params.end_iso = effectiveCustomRange.endIso;
      } else {
        params.hours = hoursToUse;
      }
      const response = await axios.get(`${API}/dashboard/equipment/${equipmentId}/trail`, { params });
      if (requestId !== trailRequestRef.current) return;
      const points = response.data?.points || [];
      if (response.data?.display_units) setTrailDisplayUnits(response.data.display_units);
      setTrailData({ equipmentId, points, hours: hoursToUse, customRange: effectiveCustomRange || null });
      setTrailVisible(true);
      // Também atualiza o allTrails para o trail global
      setAllTrails(prev => ({ ...prev, [equipmentId]: { points, hours: hoursToUse, customRange: effectiveCustomRange || null } }));
    } catch (error) {
      if (requestId !== trailRequestRef.current) return;
      console.error('Error fetching trail:', error);
      setTrailData(null);
    } finally {
      if (requestId === trailRequestRef.current) {
        setTrailLoading(false);
      }
    }
  }, [trailHours, customRangeApplied]);

  // Fetch trails for all equipments with GPS
  const fetchAllTrails = useCallback(async (customRange = null) => {
    const eqsWithLoc = (equipmentsRef.current || []).filter(eq => eq.latitude && eq.longitude);
    if (eqsWithLoc.length === 0) {
      setAllTrails({});
      return;
    }
    
    const effectiveCustomRange = customRange || customRangeApplied;
    const requestId = ++allTrailsRequestRef.current;
    setTrailLoading(true);
    try {
      const results = await Promise.allSettled(
        eqsWithLoc.map(async (eq) => {
          const params = {};
          if (trailHours === 'custom') {
            if (!effectiveCustomRange?.startIso || !effectiveCustomRange?.endIso) {
              return { id: eq.id, points: [] };
            }
            params.start_iso = effectiveCustomRange.startIso;
            params.end_iso = effectiveCustomRange.endIso;
          } else {
            params.hours = trailHours;
          }
          const response = await axios.get(`${API}/dashboard/equipment/${eq.id}/trail`, { params });
          if (response.data?.display_units) setTrailDisplayUnits(response.data.display_units);
          return { id: eq.id, points: response.data?.points || [], customRange: effectiveCustomRange || null };
        })
      );

      if (requestId !== allTrailsRequestRef.current) return;

      const newTrails = {};
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.points.length > 0) {
          newTrails[result.value.id] = { points: result.value.points, hours: trailHours, customRange: result.value.customRange || null };
        }
      }
      setAllTrails(newTrails);
    } finally {
      if (requestId === allTrailsRequestRef.current) {
        setTrailLoading(false);
      }
    }
  }, [trailHours, customRangeApplied]);

  // Toggle global trail
  const handleGlobalTrailToggle = useCallback(async () => {
    if (!globalTrailEnabled) {
      setGlobalTrailEnabled(true);
      setTrailVisible(true);
      await fetchAllTrails();
    } else {
      setGlobalTrailEnabled(false);
      setTrailVisible(false);
      setAllTrails({});
      setTrailData(null);
      trailRequestRef.current += 1;
      allTrailsRequestRef.current += 1;
      setTrailLoading(false);
    }
  }, [globalTrailEnabled, fetchAllTrails]);

  useEffect(() => {
    if (initialGlobalTrailLoadedRef.current) return;
    if (loading) return;
    if (trailHours !== '24' || customRangeApplied) return;

    const hasEquipmentWithLocation = (equipments || []).some(eq => eq.latitude && eq.longitude);
    if (!hasEquipmentWithLocation) return;

    initialGlobalTrailLoadedRef.current = true;
    setGlobalTrailEnabled(true);
    setTrailVisible(true);
    fetchAllTrails();
  }, [customRangeApplied, equipments, fetchAllTrails, loading, trailHours]);

  const handleTrailPeriodChange = useCallback((value) => {
    setTrailHours(value);
    if (value !== 'custom') {
      setCustomRangeApplied(null);
    }
  }, []);

  const handleApplyCustomRange = useCallback(async () => {
    if (!customStart || !customEnd) return;
    // Interpreta o datetime-local no fuso CONFIGURADO (rótulo "TZ:"), não no do
    // navegador — senão a janela auditada desloca em máquinas fora de Brasília.
    const startIso = naiveLocalToUtcIso(customStart, timezone);
    const endIso = naiveLocalToUtcIso(customEnd, timezone);
    if (!startIso || !endIso || startIso >= endIso) return;

    const nextRange = {
      startIso,
      endIso,
      startLocal: customStart,
      endLocal: customEnd,
    };
    setCustomRangeApplied(nextRange);

    if (globalTrailEnabled) {
      await fetchAllTrails(nextRange);
    } else if (trailData?.equipmentId) {
      await fetchTrail(trailData.equipmentId, 'custom', nextRange);
    }
  }, [customStart, customEnd, timezone, globalTrailEnabled, trailData?.equipmentId, fetchAllTrails, fetchTrail]);

  // Update trails when period changes
  useEffect(() => {
    if (trailHours === 'custom' && !customRangeApplied) return;
    if (globalTrailEnabled) {
      fetchAllTrails();
    } else if (trailData) {
      // Refresh individual trail with new period
      fetchTrail(trailData.equipmentId, trailHours);
    }
  }, [trailHours, customRangeApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh periódico de trilhas para manter dados atuais sem append local.
  useEffect(() => {
    if (!trailVisible) return undefined;
    const interval = setInterval(() => {
      if (globalTrailEnabled) {
        fetchAllTrails();
      } else if (trailData?.equipmentId) {
        fetchTrail(trailData.equipmentId, trailHours);
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [trailVisible, globalTrailEnabled, trailData?.equipmentId, trailHours, fetchAllTrails, fetchTrail]);

  // Fetch data when filters change
  useEffect(() => {
    fetchMapData();
  }, [fetchMapData]); // fetchMapData changes when activeFilters changes

  // Atualização controlada do popup selecionado (sem polling global agressivo).
  // Mantém dados do card coerentes enquanto o equipamento se move.
  useEffect(() => {
    if (!selectedEquipment?.id) return undefined;
    const interval = setInterval(() => {
      fetchSummary(selectedEquipment.id);
    }, 15000);
    return () => clearInterval(interval);
  }, [selectedEquipment?.id, fetchSummary]);

  // Socket setup (only once on mount)
  useEffect(() => {
    fetchFilters();
    
    // Fetch geofences for map overlay (v3.11.0)
    const fetchGeofences = async () => {
      try {
        const gfResponse = await geofenceApi.listGeofences(true);
        setGeofences(gfResponse.data.geofences || []);
      } catch (err) {
        console.debug('Geofences not loaded:', err.message);
        // Don't block dashboard if geofences fail
      }
    };
    fetchGeofences();

    const socket = io(process.env.REACT_APP_BACKEND_URL, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to Octopus server');
    });

    return () => {
      if (socket.connected) socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Socket event listener for equipment updates
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    const handleUpdate = (updatedEquipment) => {
      // Atualiza o equipamento diretamente no estado local
      // O evento já contém o documento completo do MongoDB
      if (!updatedEquipment || !updatedEquipment.id) return;
      
      // 1. Atualiza equipamento na lista (lógica existente)
      setEquipments(prev => {
        const index = prev.findIndex(eq => eq.id === updatedEquipment.id);
        if (index === -1) {
          // Equipamento não está na lista filtrada por RBAC — ignorar
          return prev;
        }
        // Equipamento existente - atualiza no lugar
        const updated = [...prev];
        const merged = { ...updated[index], ...updatedEquipment };
        updated[index] = {
          ...merged,
          marker_color: computeMarkerColor(merged),
        };
        return updated;
      });

      // Trails seguem reconciliação por refetch periódico para preservar desempenho.
    };

    const handleEquipmentStatus = (statusUpdate) => {
      if (!statusUpdate?.equipment_id || !statusUpdate?.status) return;

      setEquipments(prev =>
        prev.map(eq =>
          eq.id === statusUpdate.equipment_id
            ? {
                ...eq,
                status: statusUpdate.status,
                last_seen: statusUpdate.timestamp || eq.last_seen,
                marker_color: computeMarkerColor({
                  ...eq,
                  status: statusUpdate.status,
                }),
              }
            : eq
        )
      );
    };

    socket.on('equipment_update', handleUpdate);
    socket.on('equipment_status', handleEquipmentStatus);
    return () => {
      socket.off('equipment_update', handleUpdate);
      socket.off('equipment_status', handleEquipmentStatus);
    };
  }, []); // Não depende de fetchMapData

  // v3.12.0: Polling de system health para alertas de disco (apenas para admins)
  useEffect(() => {
    // Só verificar se usuário é admin
    const isAdmin = user?.role === 'admin' || user?.is_superuser;
    if (!isAdmin) return;

    const checkSystemHealth = async () => {
      try {
        const res = await axios.get(`${API}/system-health`);
        if (res.data?.alerts?.length > 0) {
          setSystemAlerts(res.data.alerts);
        } else {
          setSystemAlerts([]);
        }
      } catch (err) {
        // Silencioso - não alertar se o health check falhar
        console.debug('System health check failed:', err.message);
      }
    };

    // Check imediato + intervalo de 5 minutos
    checkSystemHealth();
    const interval = setInterval(checkSystemHealth, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Handler para dispensar alertas (apenas dismissible)
  const handleDismissAlert = (alertIndex) => {
    setDismissedAlerts(prev => new Set([...prev, alertIndex]));
  };

  // No modo seguir, clicar num equipamento adiciona/remove ele do conjunto seguido.
  const toggleFollowedMembership = (equipmentId) => {
    setFollowedIds(prev => {
      const next = new Set(prev);
      if (next.has(equipmentId)) next.delete(equipmentId);
      else next.add(equipmentId);
      return next;
    });
  };

  const handleEquipmentClick = (equipment) => {
    setSelectedEquipment(equipment);
    setSelectedFromSidebar(false);
    fetchSummary(equipment.id);
    if (followMode) toggleFollowedMembership(equipment.id);
  };

  const handleSidebarSelect = (equipment) => {
    setSelectedEquipment(equipment);
    setSelectedFromSidebar(true);
    fetchSummary(equipment.id);
    if (followMode) toggleFollowedMembership(equipment.id);
  };

  // Liga/desliga o modo seguir. Ao ligar, semeia com o equipamento já selecionado
  // (se houver) para conveniência; ao desligar, limpa o conjunto.
  const handleToggleFollowMode = () => {
    if (followMode) {
      setFollowMode(false);
      setFollowedIds(new Set());
    } else {
      setFollowMode(true);
      setFollowedIds(selectedEquipment?.id ? new Set([selectedEquipment.id]) : new Set());
    }
  };

  // Desengate disparado pelo gesto de arrastar o mapa (FollowController).
  const handleDisengageFollow = useCallback(() => {
    setFollowMode(false);
    setFollowedIds(new Set());
  }, []);

  const handleFitAll = () => {
    setSelectedEquipment(null);
    setSummaryData(null);
    setTrailData(null);
    setSelectedFromSidebar(false);
    setShouldFitBounds(true);
    // onFitComplete callback will reset shouldFitBounds
  };

  const handleFilterChange = (key, value) => {
    setActiveFilters(prev => {
      const next = { ...prev };
      if (value) {
        next[key] = value;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const clearFilters = () => {
    setActiveFilters({});
    setShowFilters(false);
  };

  const onlineCount = equipments.filter(eq => eq.status === 'online').length;
  const offlineCount = equipments.filter(eq => eq.status === 'offline').length;
  const awaitingCount = equipments.filter(eq => eq.status === 'awaiting_connection').length;
  const alertsCount = equipments.reduce((sum, eq) => sum + (eq.active_alerts_count || 0), 0);
  const equipmentsWithLocation = equipments.filter(eq => eq.latitude && eq.longitude);
  const hasActiveFilters = Object.keys(activeFilters).length > 0;
  const [hoveredTrailPoint, setHoveredTrailPoint] = useState(null);
  const equipmentsById = useMemo(
    () => new Map((equipments || []).map((eq) => [eq.id, eq])),
    [equipments]
  );

  const singleTrailHoverPoints = useMemo(() => {
    if (!trailData?.equipmentId) return [];
    return buildHoverPoints(trailData?.points || [], MAX_HOVER_POINTS)
      .map((p) => ({ ...p, equipmentId: trailData.equipmentId }));
  }, [trailData?.equipmentId, trailData?.points]);

  const globalTrailHoverPoints = useMemo(() => {
    const entries = Object.entries(allTrails);
    if (entries.length === 0) return [];
    const perEquipmentCap = Math.max(50, Math.floor(MAX_HOVER_POINTS / entries.length));
    return entries.flatMap(([equipmentId, data]) =>
      buildHoverPoints(data?.points || [], perEquipmentCap).map((p) => ({ ...p, equipmentId }))
    ).slice(0, MAX_HOVER_POINTS);
  }, [allTrails]);

  const activeTrailHoverPoints = useMemo(() => {
    if (!trailVisible) return [];
    return globalTrailEnabled ? globalTrailHoverPoints : singleTrailHoverPoints;
  }, [globalTrailEnabled, globalTrailHoverPoints, singleTrailHoverPoints, trailVisible]);

  const formatSnapshotValue = useCallback((value) => {
    if (value == null) return 'N/A';
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return 'N/A';
      return Number(value).toFixed(3);
    }
    return String(value);
  }, []);

  const fetchTelemetrySnapshot = useCallback(async (equipmentId, point) => {
    if (!equipmentId || !point?.time) return;
    setSnapshotPoint({ equipmentId, ...point });
    setSnapshotDialogOpen(true);
    setSnapshotLoading(true);
    setSnapshotData(null);
    try {
      const response = await axios.get(`${API}/dashboard/equipment/${equipmentId}/telemetry-snapshot`, {
        params: { at: point.time, window_seconds: 5 },
      });
      setSnapshotData(response.data || null);
    } catch (error) {
      console.error('Error fetching telemetry snapshot:', error);
      setSnapshotData({ telemetry: {}, error: 'Falha ao carregar snapshot' });
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  const handleTrailPointClick = useCallback((point) => {
    if (!point?.equipmentId || !point?.time) return;
    fetchTelemetrySnapshot(point.equipmentId, point);
  }, [fetchTelemetrySnapshot]);

  const hoveredTrailInfo = useMemo(() => {
    if (!hoveredTrailPoint) return null;
    const direction = hoveredTrailPoint.heading ?? hoveredTrailPoint.course;
    // Valores já na unidade de exibição (backend, preferência do sistema).
    const formatDisplay = (value, unit) => (
      value != null && !Number.isNaN(Number(value)) ? `${Number(value).toFixed(1)} ${unit || ''}`.trim() : 'N/A'
    );
    const speedValue = formatDisplay(hoveredTrailPoint.speed_display, trailDisplayUnits.speed);
    const directionValue = direction != null && !Number.isNaN(Number(direction))
      ? `${Number(direction).toFixed(0)}° ${getDirectionArrow(direction)}`
      : 'N/A';
    const eqInfo = equipmentsById.get(hoveredTrailPoint.equipmentId);
    const hasDepthSource = Boolean(eqInfo?.has_depth_source);
    const depthValue = formatDisplay(hoveredTrailPoint.depth_display, trailDisplayUnits.depth);
    return {
      ...hoveredTrailPoint,
      speedValue,
      directionValue,
      hasDepthSource,
      depthValue,
    };
  }, [equipmentsById, hoveredTrailPoint, trailDisplayUnits]);

  useEffect(() => {
    if (!trailVisible) setHoveredTrailPoint(null);
  }, [trailVisible]);

  return (
    <div className="h-screen flex flex-col md:flex-row p-4 md:p-6 gap-4 md:gap-6" data-testid="dashboard-page">
      {/* Mobile Stats */}
      <div className="md:hidden flex gap-3 overflow-x-auto pb-2">
        <StatsCard title={t('totalEquipments')} value={equipments.length} icon={OctopusIcon} />
        <StatsCard title={t('onlineEquipments')} value={onlineCount} icon={Wifi} variant="success" />
        <StatsCard title={t('offlineEquipments')} value={offlineCount} icon={WifiOff} variant="muted" />
        <StatsCard title={t('awaitingEquipments')} value={awaitingCount} icon={Cloud} variant="warning" />
      </div>

      <div className="flex-1 flex flex-col md:flex-row gap-4 md:gap-6 min-h-0">
        {/* Map Section */}
        <div className="flex-1 flex flex-col gap-4 min-h-[400px] md:min-h-0">
          {/* v3.12.0: Alertas do sistema (disco, etc) - Apenas para admins.
              GAP-3 (999.8-10): fluxo normal, fundo opaco, acima dos KPIs — sem overlay fixed/z-9999. */}
          {systemAlerts.length > 0 && (
            <div className="space-y-2">
              {systemAlerts.map((alert, index) => {
                if (dismissedAlerts.has(index)) return null;
                return (
                  <Alert
                    key={index}
                    variant={alert.type === 'error' ? 'destructive' : 'default'}
                    className="shadow-lg border-2 bg-background"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle className="flex items-center justify-between">
                      {alert.title}
                      {alert.dismissible && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 -mr-2"
                          onClick={() => handleDismissAlert(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </AlertTitle>
                    <AlertDescription>{alert.message}</AlertDescription>
                  </Alert>
                );
              })}
            </div>
          )}

          {/* Desktop Stats — contagem de colunas responsiva com wrap (a coluna do
              mapa é estreita em retrato; grid-cols-5 rígido sobrepunha os cards).
              StatsCard tem min-w-[130px], então os cards refluem para novas linhas
              em vez de esmagar/encavalar. Ver RESPONSIVE_STANDARD.md §Fileiras. */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <StatsCard title={t('totalEquipments')} value={equipments.length} icon={OctopusIcon} />
            <StatsCard title={t('onlineEquipments')} value={onlineCount} icon={Wifi} variant="success" />
            <StatsCard title={t('offlineEquipments')} value={offlineCount} icon={WifiOff} variant="muted" />
            <StatsCard title={t('awaitingEquipments')} value={awaitingCount} icon={Cloud} variant="warning" />
            <StatsCard title="Alertas Ativos" value={alertsCount} icon={AlertTriangle} variant={alertsCount > 0 ? 'danger' : 'muted'} />
          </div>

          {/* Map */}
          <Card className="flex-1 overflow-hidden relative" data-testid="map-container">
            {/* Trail controls - aligned with top of zoom controls */}
            <div className="absolute top-[10px] left-12 z-[1000] flex items-center gap-2">
              {/* Trail Period Selector */}
              <Select value={trailHours} onValueChange={handleTrailPeriodChange}>
                <SelectTrigger className="w-[80px] h-8 text-xs bg-background/90 backdrop-blur">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1h</SelectItem>
                  <SelectItem value="4">4h</SelectItem>
                  <SelectItem value="12">12h</SelectItem>
                  <SelectItem value="24">24h</SelectItem>
                  <SelectItem value="48">48h</SelectItem>
                  <SelectItem value="72">72h</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>

              {trailHours === 'custom' && (
                <div className="flex items-center gap-1 rounded-md bg-background/90 backdrop-blur px-2 py-1 border border-border">
                  <Input
                    type="datetime-local"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="h-8 w-[170px] text-xs"
                  />
                  <Input
                    type="datetime-local"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="h-8 w-[170px] text-xs"
                  />
                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    onClick={handleApplyCustomRange}
                    disabled={!customStart || !customEnd || trailLoading}
                  >
                    Aplicar
                  </Button>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">TZ: {timezoneLabel}</span>
                </div>
              )}
              
              {/* Global Trail Toggle - Always visible */}
              <Button
                size="sm" 
                variant={globalTrailEnabled ? "default" : "secondary"}
                className={cn("gap-1", globalTrailEnabled && "bg-purple-600 hover:bg-purple-700")}
                onClick={handleGlobalTrailToggle}
                disabled={trailLoading}
                data-testid="global-trail-btn"
              >
                {trailLoading ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Route className="w-4 h-4" />
                )}
                Trajetos
              </Button>
              
              {selectedEquipment && (
                <Button
                  size="sm" variant="secondary"
                  className="gap-2"
                  onClick={handleFitAll} data-testid="fit-all-btn"
                >
                  <Maximize2 className="w-4 h-4" />
                  Ver Todos
                </Button>
              )}

              {/* Modo Seguir: clique nos equipamentos para adicionar/remover do conjunto */}
              <Button
                size="sm"
                variant={followMode ? "default" : "secondary"}
                className={cn("gap-1", followMode && "bg-sky-600 hover:bg-sky-700")}
                onClick={handleToggleFollowMode}
                data-testid="follow-mode-btn"
                title={followMode ? "Clique nos equipamentos para seguir/parar; arrastar o mapa desliga" : "Seguir equipamentos (acompanha o movimento)"}
              >
                <Navigation className="w-4 h-4" />
                {followMode ? `Seguindo (${followedIds.size})` : 'Seguir'}
              </Button>
            </div>
            {/* Legenda das linhas de direção do marcador (proa vs curso) */}
            <div className="absolute bottom-2 left-2 z-[1000] rounded-md bg-background/90 backdrop-blur px-2 py-1.5 border border-border text-[10px] leading-tight space-y-1 pointer-events-none">
              <div className="flex items-center gap-1.5">
                <svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" /></svg>
                <span className="text-muted-foreground">Proa (heading)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg width="18" height="6"><line x1="0" y1="3" x2="18" y2="3" stroke="#f97316" strokeWidth="3" strokeLinecap="round" strokeDasharray="5 3" /></svg>
                <span className="text-muted-foreground">Curso (COG)</span>
              </div>
              {followMode && (
                <div className="flex items-center gap-1.5">
                  <svg width="18" height="10"><circle cx="9" cy="5" r="4" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="2 2" /></svg>
                  <span className="text-muted-foreground">Seguindo</span>
                </div>
              )}
            </div>
            <MapContainer
              center={[-23.5505, -46.6333]} zoom={10}
              className="h-full w-full min-h-[300px]"
              style={{ background: '#020617' }}
              ref={mapRef}
            >
              <LayersControl position="topright">
                <BaseLayer name="Mapa Escuro">
                  <TileLayer attribution='&copy; CARTO' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                </BaseLayer>
                <BaseLayer name="Mapa Padrao">
                  <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                </BaseLayer>
                <BaseLayer checked name="Satelite">
                  <TileLayer attribution='&copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                </BaseLayer>
              </LayersControl>
              <FitBoundsController
                equipments={equipmentsWithLocation}
                selectedEquipment={selectedEquipment}
                shouldFitBounds={shouldFitBounds}
                selectedFromSidebar={selectedFromSidebar}
                onFitComplete={() => setShouldFitBounds(false)}
              />
              <FollowController
                equipments={equipmentsWithLocation}
                followedIds={followedIds}
                onDisengage={handleDisengageFollow}
              />

              {/* Geofence overlay (polygons + waypoints) */}
              {geofences.map(gf => {
                const geometryType = gf.geometry_type || (gf.point ? 'waypoint' : 'polygon');

                if (geometryType === 'waypoint' && Array.isArray(gf.point?.coordinates) && gf.point.coordinates.length >= 2) {
                  const [lon, lat] = gf.point.coordinates;
                  return (
                    <Marker
                      key={`geofence-waypoint-${gf.id}`}
                      position={[lat, lon]}
                      icon={createGeofenceWaypointIcon(gf.color || '#0EA5E9')}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="flex items-center gap-2 font-semibold">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: gf.color }}
                            />
                            {gf.name}
                          </div>
                          {gf.description && (
                            <p className="text-xs text-gray-500 mt-1">{gf.description}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">Waypoint</p>
                        </div>
                      </Popup>
                    </Marker>
                  );
                }

                if (!gf.polygon?.coordinates?.[0]) return null;
                const positions = gf.polygon.coordinates[0].map(([lon, lat]) => [lat, lon]);
                return (
                  <Polygon
                    key={`geofence-${gf.id}`}
                    positions={positions}
                    pathOptions={{
                      ...getGeofencePathOptions(gf, 0.15)
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <div className="flex items-center gap-2 font-semibold">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: gf.color }}
                          />
                          {gf.name}
                        </div>
                        {gf.description && (
                          <p className="text-xs text-gray-500 mt-1">{gf.description}</p>
                        )}
                      </div>
                    </Popup>
                  </Polygon>
                );
              })}
              
              {equipmentsWithLocation.map((eq) => (
                <Marker
                  key={eq.id}
                  position={[eq.latitude, eq.longitude]}
                  icon={createEquipmentIcon(eq.equipment_type_name, eq.marker_color, eq.heading, eq.cog, eq.speed_knots, followedIds.has(eq.id))}
                  eventHandlers={{ click: () => handleEquipmentClick(eq) }}
                >
                  <Popup maxWidth={460} minWidth={340} autoPanPadding={[24, 24]}>
                    <SummaryPopup
                      equipment={eq}
                      summaryData={selectedEquipment?.id === eq.id ? summaryData : null}
                      loading={selectedEquipment?.id === eq.id && summaryLoading}
                      navigate={navigate}
                      t={t}
                      formatDateTime={formatDateTime}
                      onShowTrail={fetchTrail}
                      trailLoading={trailLoading}
                      hasTrail={trailData?.equipmentId === eq.id && trailData.points.length > 0}
                      trailActive={trailVisible && (trailData?.equipmentId === eq.id || allTrails[eq.id])}
                      onHideTrail={() => {
                        if (trailData?.equipmentId === eq.id) {
                          setTrailData(null);
                          setTrailVisible(false);
                        }
                        setAllTrails(prev => {
                          const newTrails = { ...prev };
                          delete newTrails[eq.id];
                          return newTrails;
                        });
                      }}
                    />
                  </Popup>
                </Marker>
              ))}
              
              {/* Individual trail (from popup) */}
              {trailVisible && trailData && trailData.points.length > 1 && !globalTrailEnabled && (
                splitTrailSegments(trailData.points).map((segment, idx) => (
                  <Polyline
                    key={`trail-single-${trailData.equipmentId}-${idx}`}
                    positions={segment}
                    pathOptions={getTrailPathOptions()}
                    eventHandlers={{
                      mouseover: (e) => setTrailCursor(e, 'crosshair'),
                      mouseout: (e) => setTrailCursor(e, ''),
                    }}
                  />
                ))
              )}

              {/* All trails (global mode) */}
              {trailVisible && globalTrailEnabled && Object.entries(allTrails).map(([eqId, data]) => (
                splitTrailSegments(data.points).map((segment, idx) => (
                  <Polyline
                    key={`trail-${eqId}-${idx}`}
                    positions={segment}
                    pathOptions={getTrailPathOptions()}
                    eventHandlers={{
                      mouseover: (e) => setTrailCursor(e, 'crosshair'),
                      mouseout: (e) => setTrailCursor(e, ''),
                    }}
                  />
                ))
              ))}

              <TrailHoverController
                enabled={trailVisible && activeTrailHoverPoints.length > 0}
                points={activeTrailHoverPoints}
                onHoverChange={setHoveredTrailPoint}
                onPointClick={handleTrailPointClick}
              />

              {trailVisible && hoveredTrailInfo && (
                <CircleMarker
                  center={[hoveredTrailInfo.lat, hoveredTrailInfo.lon]}
                  radius={6}
                  pathOptions={{
                    color: '#ffffff',
                    weight: 2,
                    fillColor: '#a855f7',
                    fillOpacity: 0.9,
                    opacity: 1,
                  }}
                  eventHandlers={{
                    click: () => handleTrailPointClick(hoveredTrailInfo),
                  }}
                >
                  <Tooltip direction="top" offset={[0, -12]} opacity={0.96} permanent>
                    <div className="text-xs leading-5 min-w-[180px]">
                      <div><strong>{formatDateTime(hoveredTrailInfo.time, { omitSeconds: true })}</strong></div>
                      <div>Vel: {hoveredTrailInfo.speedValue}</div>
                      <div>Rumo: {hoveredTrailInfo.directionValue}</div>
                      {hoveredTrailInfo.hasDepthSource && <div>Prof: {hoveredTrailInfo.depthValue}</div>}
                    </div>
                  </Tooltip>
                </CircleMarker>
              )}
            </MapContainer>
          </Card>
        </div>

        {/* Sidebar */}
        <Card className="w-full md:w-80 flex flex-col max-h-[300px] md:max-h-none" data-testid="equipment-sidebar">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t('equipments')}</CardTitle>
              <div className="flex gap-1">
                <Button
                  size="sm" variant={showFilters ? 'default' : 'outline'}
                  className="gap-1 relative"
                  onClick={() => setShowFilters(!showFilters)}
                  data-testid="toggle-filters-btn"
                >
                  <Filter className="w-4 h-4" />
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-[10px] rounded-full flex items-center justify-center">
                      {Object.keys(activeFilters).length}
                    </span>
                  )}
                </Button>
                <Link to="/equipments">
                  <Button size="sm" variant="outline" className="gap-2" data-testid="add-equipment-link">
                    <Plus className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Filters Panel */}
            {showFilters && filters && (
              <div className="mt-3 space-y-2 p-3 bg-secondary/50 rounded-lg" data-testid="filters-panel">
                <FilterSelect
                  label="Status Operacional"
                  value={activeFilters.operational_status || ''}
                  options={filters.operational_statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                  onChange={(v) => handleFilterChange('operational_status', v)}
                  testId="filter-operational-status"
                />
                <FilterSelect
                  label="Tipo"
                  value={activeFilters.category || ''}
                  options={filters.categories.map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))}
                  onChange={(v) => handleFilterChange('category', v)}
                  testId="filter-category"
                />
                <FilterSelect
                  label="Grupo"
                  value={activeFilters.equipment_group_id || ''}
                  options={filters.equipment_groups.map(g => ({ value: g.id, label: g.name }))}
                  onChange={(v) => handleFilterChange('equipment_group_id', v)}
                  testId="filter-group"
                />
                {hasActiveFilters && (
                  <Button size="sm" variant="ghost" className="w-full gap-1 text-xs" onClick={clearFilters} data-testid="clear-filters-btn">
                    <X className="w-3 h-3" /> Limpar Filtros
                  </Button>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden">
            <ScrollArea className="h-full px-4 pb-4">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : equipments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <Ship className="w-8 h-8 mb-2 opacity-50" />
                  <p>{t('noEquipments')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {equipments.map((eq) => (
                    <EquipmentListItem
                      key={eq.id}
                      equipment={eq}
                      isSelected={selectedEquipment?.id === eq.id}
                      onClick={() => handleSidebarSelect(eq)}
                    />
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <Dialog open={snapshotDialogOpen} onOpenChange={setSnapshotDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Snapshot de Telemetria</DialogTitle>
            <DialogDescription>
              {snapshotPoint?.time
                ? `Ponto: ${formatDateTime(snapshotPoint.time)} (${timezoneLabel})`
                : 'Selecione um ponto do trajeto para inspecionar a telemetria.'}
            </DialogDescription>
          </DialogHeader>

          {snapshotLoading ? (
            <div className="py-8 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                <div>Measurement: {snapshotData?.source_measurement || 'N/A'}</div>
                <div>Amostra mais próxima: {snapshotData?.matched_time ? formatDateTime(snapshotData.matched_time) : 'N/A'}</div>
                <div>Delta: {snapshotData?.delta_ms != null ? `${snapshotData.delta_ms} ms` : 'N/A'}</div>
              </div>

              <ScrollArea className="h-[360px] border rounded-md p-3">
                {Object.keys(snapshotData?.telemetry || {}).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem dados no intervalo solicitado.</p>
                ) : (
                  <div className="space-y-1">
                    {Object.entries(snapshotData.telemetry)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([key, value]) => (
                        <div key={key} className="grid grid-cols-[1fr_auto] gap-3 text-xs py-1 border-b border-border/40 last:border-b-0">
                          <span className="text-muted-foreground break-all">{key}</span>
                          <span className="font-medium">{formatSnapshotValue(value)}</span>
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Sub Components ---

const StatsCard = ({ title, value, icon: Icon, variant = 'default', className }) => {
  const colors = {
    default: 'text-primary',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    muted: 'text-muted-foreground',
    danger: 'text-red-500',
  };
  // Layout proporcional (RESPONSIVE_STANDARD.md §Tipografia ↔ container):
  // valor ao lado do ícone na linha de cima; título NA PRÓPRIA LINHA usando a
  // largura CHEIA do card, com text-sm + leading-tight — assim títulos longos
  // ("Total de Equipamentos") quebram limpo em 2 linhas justas em vez de espremer
  // num text-xs ao lado do número. min-w-[130px] preservado (subir reabriria a
  // sobreposição da grade de KPIs no breakpoint de 3 colunas).
  return (
    <Card className={cn('p-4 min-w-[130px]', className)}>
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded-lg bg-secondary shrink-0', colors[variant])}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="text-2xl font-bold leading-none tabular-nums">{value}</p>
      </div>
      <p className="mt-2 text-sm font-medium text-muted-foreground leading-tight">
        {title}
      </p>
    </Card>
  );
};

const FilterSelect = ({ label, value, options, onChange, testId }) => (
  <div>
    <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
    <select
      className="w-full bg-background border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    >
      <option value="">Todos</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);

const EquipmentTypeIcon = ({ typeName, className }) => {
  const iconKey = getIconKeyFromTypeName(typeName);
  const svgPath = customIconSvgs[iconKey] || customIconSvgs.settings;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <g dangerouslySetInnerHTML={{ __html: svgPath }} />
    </svg>
  );
};

const EquipmentListItem = ({ equipment, isSelected, onClick }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const color = MARKER_COLORS[equipment.marker_color] || MARKER_COLORS.gray;
  // Velocidade já na unidade de exibição (map-data, backend).
  const speedDisplay = equipment.speed_display;
  const speedText = speedDisplay?.value != null
    ? `${Number(speedDisplay.value).toFixed(1)} ${speedDisplay.unit || ''}`.trim()
    : '--';
  const statusLabel = getEquipmentStatusLabel(equipment.status, t);
  // Offline: mostra quando foi o último contato (last_seen do backend, no fuso do sistema).
  const lastUpdateLabel = equipment.status === 'offline' && equipment.last_seen
    ? formatShortDateTime(equipment.last_seen, timezone)
    : null;

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full p-3 rounded-lg text-left transition-all duration-200 hover:bg-secondary/50',
        isSelected && 'bg-primary/20 border-l-2 border-primary'
      )}
      data-testid={`equipment-item-${equipment.id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}33`, color }}>
          <EquipmentTypeIcon typeName={equipment.equipment_type_name} className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{equipment.name}</p>
          <p className="text-xs text-muted-foreground truncate">{equipment.tag}</p>
          {equipment.status === 'awaiting_connection' && (
            <p className="text-[10px] text-yellow-400 truncate">{statusLabel}</p>
          )}
          {/* Quebra antes da data em vez de truncar: com a velocidade na coluna da direita,
              "Última atualização: dd/mm/aaaa hh:mm" não cabe numa linha e a data sumia. */}
          {lastUpdateLabel && (
            <p className="text-[10px] leading-tight text-muted-foreground" data-testid={`equipment-last-update-${equipment.id}`}>
              {t('lastUpdate')}: <span className="whitespace-nowrap">{lastUpdateLabel}</span>
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} title={statusLabel} />
          {equipment.active_alerts_count > 0 && (
            <Badge variant="destructive" className="text-[10px] px-1 py-0">
              {equipment.active_alerts_count}
            </Badge>
          )}
          {equipment.speed_knots != null && equipment.speed_knots > 0.5 && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
              <Navigation className="w-3 h-3" style={{ transform: `rotate(${equipment.cog || 0}deg)` }} />
              {speedText}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const SummaryPopup = ({
  equipment,
  summaryData,
  loading,
  navigate,
  t,
  formatDateTime,
  onShowTrail,
  trailLoading,
  hasTrail,
  trailActive,
  onHideTrail,
}) => {
  const data = { ...equipment, ...(summaryData || {}) };
  const keyTelemetry = summaryData?.key_telemetry || {};
  // {value, unit} já na preferência de unidades — conversão só no backend
  // (INV-LAYER-READPATH-001 / INV-UI-TRAIL-001).
  const keyDisplay = summaryData?.key_telemetry_display || {};
  const hasDepthSource = Boolean(
    equipment?.has_depth_source ||
    summaryData?.has_depth_source ||
    Object.prototype.hasOwnProperty.call(keyTelemetry, 'depth')
  );
  const hasEngineLoadSource = Boolean(
    summaryData?.has_engine_load_source ||
    Object.prototype.hasOwnProperty.call(keyTelemetry, 'port_engine_load') ||
    Object.prototype.hasOwnProperty.call(keyTelemetry, 'starboard_engine_load') ||
    Object.prototype.hasOwnProperty.call(keyTelemetry, 'main_engine_load') ||
    Object.prototype.hasOwnProperty.call(keyTelemetry, 'engine_load')
  );
  const toNumber = (value) => {
    if (value == null) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  // Renderiza {value, unit} do backend; nunca converte.
  const formatMetric = (metric) => {
    const parsed = toNumber(metric?.value);
    if (parsed == null) return '--';
    return `${parsed.toFixed(1)} ${metric?.unit || ''}`.trim();
  };

  const formatWind = (speedMetric, directionMetric) => {
    const speedText = formatMetric(speedMetric);
    const directionNumber = toNumber(directionMetric?.value);
    const directionText = directionNumber == null
      ? '--'
      : `${getDirectionArrow(directionNumber)} ${directionNumber.toFixed(0)}deg`;
    return `${directionText} | ${speedText}`;
  };

  const hasPortEngineLoadField = Object.prototype.hasOwnProperty.call(keyTelemetry, 'port_engine_load');
  const hasStarboardEngineLoadField = Object.prototype.hasOwnProperty.call(keyTelemetry, 'starboard_engine_load');
  const twinEngineLoadLayout = hasPortEngineLoadField || hasStarboardEngineLoadField;

  const popupRows = [
    { key: 'port_rpm', label: 'port rpm', value: formatMetric(keyDisplay.port_rpm) },
    { key: 'starboard_rpm', label: 'starboard rpm', value: formatMetric(keyDisplay.starboard_rpm) },
    ...(hasEngineLoadSource
      ? twinEngineLoadLayout
        ? [
            { key: 'port_engine_load', label: 'port engine load', value: formatMetric(keyDisplay.port_engine_load) },
            { key: 'starboard_engine_load', label: 'starboard engine load', value: formatMetric(keyDisplay.starboard_engine_load) },
          ]
        : [
            {
              key: 'engine_load',
              label: 'engine load',
              value: formatMetric(keyDisplay.main_engine_load ?? keyDisplay.engine_load),
            },
          ]
      : []),
    { key: 'port_temp', label: 'port temp', value: formatMetric(keyDisplay.port_temp) },
    { key: 'starboard_temp', label: 'starboard temp', value: formatMetric(keyDisplay.starboard_temp) },
    { key: 'wind_speed', label: 'wind', value: formatWind(keyDisplay.wind_speed, keyDisplay.wind_direction) },
    { key: 'sog', label: 'sog', value: formatMetric(keyDisplay.sog ?? data.speed_display) },
    ...(hasDepthSource
      ? [{ key: 'depth', label: 'depth', value: formatMetric(keyDisplay.depth) }]
      : []),
  ];
  const popupGridRows = [
    ['port_rpm', 'starboard_rpm'],
    ...(hasEngineLoadSource
      ? twinEngineLoadLayout
        ? [['port_engine_load', 'starboard_engine_load']]
        : [['engine_load', null]]
      : []),
    ['port_temp', 'starboard_temp'],
    ['wind_speed', 'sog'],
    ...(hasDepthSource ? [['depth', null]] : []),
  ];
  const popupRowsByKey = popupRows.reduce((acc, row) => {
    acc[row.key] = row;
    return acc;
  }, {});
  const statusLabel = getEquipmentStatusLabel(data.status, t);

  return (
    <div className="p-2 w-[380px] max-w-[90vw]" data-testid={`popup-summary-${equipment.id}`}>
      <h3 className="font-bold text-lg">{data.name}</h3>
      <p className="text-sm opacity-80">{data.tag}</p>
      <div className="flex items-center gap-2 mt-2">
        <Badge
          variant={data.status === 'online' ? 'default' : 'secondary'}
          className={cn(
            data.status === 'awaiting_connection' && 'border-yellow-500/50 text-yellow-300 bg-yellow-500/10'
          )}
        >
          {statusLabel}
        </Badge>
        <span className="text-xs opacity-70">{equipment.equipment_type_name}</span>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-3">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Key Telemetry */}
      {popupRows.length > 0 && (
        <div className="mt-2 space-y-1.5 text-xs">
          {popupGridRows.map(([leftKey, rightKey], rowIndex) => (
            <div key={`${leftKey}-${rightKey ?? 'empty'}-${rowIndex}`} className="grid grid-cols-2 gap-1.5">
              {[leftKey, rightKey].map((metricKey, colIndex) => {
                if (!metricKey) {
                  return <div key={`empty-${rowIndex}-${colIndex}`} />;
                }

                const row = popupRowsByKey[metricKey];
                if (!row) {
                  return <div key={`missing-${metricKey}-${rowIndex}-${colIndex}`} />;
                }

                return (
                  <div key={row.key} className="min-w-0 bg-secondary/50 rounded px-2 py-1 min-h-[46px]">
                    <div className="opacity-70 leading-tight truncate">{row.label}</div>
                    <div className="font-medium leading-tight mt-0.5 break-words">{row.value}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Active Alerts */}
      {summaryData?.active_alerts?.length > 0 && (
        <div className="mt-2 space-y-1">
          {summaryData.active_alerts.slice(0, 3).map((alert) => (
            <div key={alert.id} className="flex items-center gap-1 text-xs text-red-400">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{alert.message || alert.type}</span>
            </div>
          ))}
        </div>
      )}

      {/* Groups */}
      {summaryData?.groups?.length > 0 && (
        <p className="text-[10px] text-muted-foreground mt-1">
          {summaryData.groups.join(', ')}
        </p>
      )}

      {data.last_seen && (
        <p className="text-xs opacity-60 mt-2">
          {t('lastSeen')}: {formatDateTime(data.last_seen)}
        </p>
      )}

      <div className="flex gap-2 mt-3 pt-2 border-t border-gray-600">
        <Button
          size="sm" variant="default" className="flex-1 gap-1"
          onClick={() => navigate(`/equipments/${equipment.id}/monitor`)}
          data-testid={`popup-live-${equipment.id}`}
        >
          <Activity className="w-3 h-3" /> Ao Vivo
        </Button>
        <Button
          size="sm" 
          variant={trailActive ? "default" : "outline"} 
          className={cn("flex-1 gap-1", trailActive && "bg-purple-600 hover:bg-purple-700")}
          onClick={() => trailActive ? onHideTrail() : onShowTrail(equipment.id)}
          disabled={trailLoading}
          data-testid={`popup-trail-${equipment.id}`}
        >
          <Route className="w-3 h-3" />
          {trailLoading ? '...' : trailActive ? 'Ocultar' : 'Trajeto'}
        </Button>
      </div>
      <div className="flex gap-2 mt-1">
        <Button
          size="sm" variant="ghost" className="flex-1 gap-1 text-xs"
          onClick={() => navigate(`/equipments/${equipment.id}/monitor?tab=historico`)}
          data-testid={`popup-history-${equipment.id}`}
        >
          <History className="w-3 h-3" /> Historico
        </Button>
      </div>
    </div>
  );
};

export default DashboardPage;
